import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import { getAuthUser } from '../utils/auth';

const statusLabels = {
  REQUESTED: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  APPROVED: { label: 'Aprobada', color: 'bg-green-100 text-green-700' },
  REJECTED: { label: 'Rechazada', color: 'bg-red-100 text-red-700' },
};

export default function OvertimeBoard() {
  const authUser = getAuthUser();
  const isEmployee = authUser?.role === 'EMPLOYEE';
  const isAdmin = ['ADMIN', 'HR'].includes(authUser?.role);
  const isHr = authUser?.role === 'HR';
  const currentEmployeeId = authUser?.employeeId ?? null;
  const [overtimes, setOvertimes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterError, setFilterError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({
    employeeId: '',
    date: '',
    hours: '',
    minutes: '',
    reason: '',
  });

  const employeeMap = useMemo(() => {
    return new Map(employees.map((employee) => [employee.id, employee]));
  }, [employees]);

  const hrDepartmentId = useMemo(() => {
    if (!isHr || !currentEmployeeId) {
      return null;
    }
    const hrEmployee = employees.find((employee) => employee.id === currentEmployeeId);
    return hrEmployee?.departmentId ?? null;
  }, [employees, isHr, currentEmployeeId]);

  const scopedEmployees =
    isHr && hrDepartmentId
      ? employees.filter((employee) => employee.departmentId === hrDepartmentId)
      : employees;

  useEffect(() => {
    if (isHr && hrDepartmentId) {
      setDepartmentFilter(String(hrDepartmentId));
    }
  }, [isHr, hrDepartmentId]);

  const rows = useMemo(() => {
    const baseRows = overtimes.map((overtime) => {
      const employee = employeeMap.get(overtime.employeeId);
      return {
        ...overtime,
        departmentId: employee?.departmentId ?? overtime.departmentId ?? null,
        employeeName:
          overtime.employee?.firstName || overtime.employee?.lastName
            ? `${overtime.employee?.firstName ?? ''} ${overtime.employee?.lastName ?? ''}`.trim()
            : employee
              ? `${employee.firstName} ${employee.lastName}`
              : overtime.employeeName || `Empleado ${overtime.employeeId}`,
      };
    });
    return isHr && hrDepartmentId
      ? baseRows.filter((row) => row.departmentId === hrDepartmentId)
      : baseRows;
  }, [overtimes, employeeMap, isHr, hrDepartmentId]);

  useEffect(() => {
    const loadOvertime = async () => {
      setIsLoading(true);
      setError(null);
      setFilterError(null);
      if (isAdmin && dateFrom && dateTo && dateFrom > dateTo) {
        setFilterError('La fecha "desde" no puede ser posterior a "hasta".');
        setOvertimes([]);
        setIsLoading(false);
        return;
      }
      try {
        const params = {};
        if (isAdmin && dateFrom) {
          params.from = dateFrom;
        }
        if (isAdmin && dateTo) {
          params.to = dateTo;
        }
        if (isAdmin && employeeFilter) {
          params.employeeId = employeeFilter;
        }
        if (isAdmin && departmentFilter) {
          params.departmentId = departmentFilter;
        }
        if (isAdmin && statusFilter) {
          params.status = statusFilter;
        }
        if (isEmployee && currentEmployeeId) {
          const [overtimeResponse, employeeResponse] = await Promise.all([
            axios.get('/overtime', { params }),
            axios.get(`/employees/${currentEmployeeId}`),
          ]);
          setOvertimes(overtimeResponse.data);
          setEmployees([employeeResponse.data]);
          setDepartments([]);
        } else {
          if (isAdmin) {
            const [overtimeResponse, employeesResponse, departmentsResponse] = await Promise.all([
              axios.get('/overtime', { params }),
              axios.get('/employees'),
              axios.get('/departments'),
            ]);
            setOvertimes(overtimeResponse.data);
            setEmployees(employeesResponse.data);
            setDepartments(departmentsResponse.data);
          } else {
            const [overtimeResponse, employeesResponse] = await Promise.all([
              axios.get('/overtime', { params }),
              axios.get('/employees'),
            ]);
            setOvertimes(overtimeResponse.data);
            setEmployees(employeesResponse.data);
            setDepartments([]);
          }
        }
      } catch (err) {
        const message = err.response?.data?.message || 'No se pudieron cargar las horas extras.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadOvertime();
  }, [
    isEmployee,
    currentEmployeeId,
    isAdmin,
    dateFrom,
    dateTo,
    employeeFilter,
    departmentFilter,
    statusFilter,
  ]);

  useEffect(() => {
    if (isEmployee && currentEmployeeId) {
      setForm((prev) => ({ ...prev, employeeId: currentEmployeeId.toString() }));
    }
  }, [isEmployee, currentEmployeeId]);

  const reloadOvertime = async () => {
    const params = {};
    if (isAdmin && dateFrom) {
      params.from = dateFrom;
    }
    if (isAdmin && dateTo) {
      params.to = dateTo;
    }
    if (isAdmin && employeeFilter) {
      params.employeeId = employeeFilter;
    }
    if (isAdmin && departmentFilter) {
      params.departmentId = departmentFilter;
    }
    if (isAdmin && statusFilter) {
      params.status = statusFilter;
    }
    const response = await axios.get('/overtime', { params });
    setOvertimes(response.data);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      employeeId: isEmployee ? currentEmployeeId?.toString() ?? '' : '',
      date: '',
      hours: '',
      minutes: '',
      reason: '',
    });
    setEditingId(null);
  };

  const toMinutes = (hoursValue, minutesValue) => {
    const hours = Number(hoursValue);
    const minutes = Number(minutesValue);
    const safeHours = Number.isFinite(hours) ? hours : 0;
    const safeMinutes = Number.isFinite(minutes) ? minutes : 0;
    return Math.max(0, safeHours) * 60 + Math.max(0, safeMinutes);
  };

  const formatDuration = (totalMinutes) => {
    const safeMinutes = Number.isFinite(totalMinutes) ? totalMinutes : 0;
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setIsSaving(true);

    const resolvedEmployeeId = isEmployee ? currentEmployeeId : Number(form.employeeId);
    if (!resolvedEmployeeId) {
      setError('Usuario sin empleado asociado.');
      setIsSaving(false);
      return;
    }

    const payload = {
      employeeId: resolvedEmployeeId,
      date: form.date,
      minutes: toMinutes(form.hours, form.minutes),
      reason: form.reason || undefined,
    };

    try {
      if (editingId) {
        await axios.patch(`/overtime/${editingId}`, {
          date: payload.date,
          minutes: payload.minutes,
          reason: payload.reason,
        });
      } else {
        await axios.post('/overtime', payload);
      }
      await reloadOvertime();
      resetForm();
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo guardar la solicitud.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (overtime) => {
    const totalMinutes = Number(overtime.minutes) || 0;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    setEditingId(overtime.id);
    setForm({
      employeeId: overtime.employeeId,
      date: overtime.date,
      hours: String(hours),
      minutes: String(minutes),
      reason: overtime.reason || '',
    });
  };

  const handleDelete = async (overtimeId) => {
    const confirmDelete = window.confirm('¿Seguro que deseas eliminar esta solicitud?');
    if (!confirmDelete) {
      return;
    }
    setIsDeletingId(overtimeId);
    setError(null);
    try {
      await axios.delete(`/overtime/${overtimeId}`);
      await reloadOvertime();
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo eliminar la solicitud.';
      setError(message);
    } finally {
      setIsDeletingId(null);
    }
  };

  const updateStatus = async (overtimeId, status) => {
    try {
      if (status === 'APPROVED') {
        await axios.patch(`/overtime/${overtimeId}/approve`);
      } else {
        await axios.patch(`/overtime/${overtimeId}/reject`);
      }
      await reloadOvertime();
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo actualizar la solicitud.';
      setError(message);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Horas extras</h1>
        <p className="text-sm text-gray-500">Gestion de solicitudes manuales.</p>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}
      {filterError && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">
          {filterError}
        </div>
      )}

      {isAdmin && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="overtimeFrom">
                Desde
              </label>
              <input
                id="overtimeFrom"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="overtimeTo">
                Hasta
              </label>
              <input
                id="overtimeTo"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="overtimeEmployee">
                Empleado
              </label>
              <select
                id="overtimeEmployee"
                value={employeeFilter}
                onChange={(event) => setEmployeeFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Todos</option>
                {scopedEmployees.map((employee) => (
                  <option key={employee.id} value={String(employee.id)}>
                    {employee.firstName} {employee.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="overtimeDepartment">
                Departamento
              </label>
              <select
                id="overtimeDepartment"
                value={departmentFilter}
                onChange={(event) => setDepartmentFilter(event.target.value)}
                disabled={isHr}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Todos</option>
                {(isHr && hrDepartmentId
                  ? departments.filter((department) => department.id === hrDepartmentId)
                  : departments
                ).map((department) => (
                  <option key={department.id} value={String(department.id)}>
                    {department.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-semibold text-gray-700" htmlFor="overtimeStatus">
                Estado
              </label>
              <select
                id="overtimeStatus"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Todos</option>
                {Object.keys(statusLabels).map((status) => (
                  <option key={status} value={status}>
                    {statusLabels[status].label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800">
          {editingId ? 'Editar solicitud' : 'Nueva solicitud'}
        </h2>
        <p className="text-xs text-gray-500">Completa la informacion requerida.</p>
        <form onSubmit={handleSubmit} className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-gray-600">Empleado</label>
            {isEmployee ? (
              <input
                type="text"
                disabled
                value={
                  employeeMap.has(currentEmployeeId)
                    ? `${employeeMap.get(currentEmployeeId).firstName} ${employeeMap.get(currentEmployeeId).lastName}`
                    : 'Empleado asignado'
                }
                className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 p-2 text-gray-500"
              />
            ) : (
              <select
                name="employeeId"
                value={form.employeeId}
                onChange={handleFormChange}
                required
                className="mt-1 w-full rounded-lg border border-gray-200 p-2"
              >
                <option value="">Selecciona</option>
                {scopedEmployees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.firstName} {employee.lastName}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600">Fecha</label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleFormChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600">Horas</label>
            <input
              type="number"
              name="hours"
              min="0"
              value={form.hours}
              onChange={handleFormChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600">Minutos</label>
            <input
              type="number"
              name="minutes"
              min="0"
              max="59"
              value={form.minutes}
              onChange={handleFormChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-semibold text-gray-600">Motivo</label>
            <textarea
              name="reason"
              value={form.reason}
              onChange={handleFormChange}
              className="mt-1 w-full rounded-lg border border-gray-200 p-2"
              rows="3"
              placeholder="Opcional"
            />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-60"
            >
              {isSaving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear solicitud'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg border border-gray-200 px-4 py-2 text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Cargando horas extras...</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Colaborador
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Tiempo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Motivo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((overtime) => {
                const statusInfo = statusLabels[overtime.status] ?? statusLabels.REQUESTED;
                const canEdit = overtime.status === 'REQUESTED';
                const canDelete = !isEmployee || overtime.status === 'REQUESTED';
                return (
                  <tr key={overtime.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {overtime.employeeName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{overtime.date}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDuration(overtime.minutes)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{overtime.reason || '--'}</td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${statusInfo.color}`}
                      >
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-wrap gap-2">
                        {!isEmployee ? (
                          overtime.status === 'REQUESTED' ? (
                            <>
                              <button
                                onClick={() => updateStatus(overtime.id, 'APPROVED')}
                                className="rounded bg-green-600 px-3 py-1 text-xs font-semibold text-white hover:bg-green-700"
                              >
                                Aprobar
                              </button>
                              <button
                                onClick={() => updateStatus(overtime.id, 'REJECTED')}
                                className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700"
                              >
                                Rechazar
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">Sin acciones</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">Sin acciones</span>
                        )}
                        <button
                          onClick={() => handleEdit(overtime)}
                          disabled={!canEdit}
                          className="rounded bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(overtime.id)}
                          disabled={!canDelete || isDeletingId === overtime.id}
                          className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                        >
                          {isDeletingId === overtime.id ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
